// jmix-exit E3b.1: maps the SPA JWT `authorities` claim → the single nav `Role` the shell keys on.
// React-free + pure so it is unit-testable in isolation (like shell/nav.ts), replacing the Epic-3a
// dev role-switch (auth/stubAuth.ts). The authority strings are the seeded `ROLE.NAME`s prefixed
// `ROLE_` (jmix-1-3 dev-seed: system-full-access / foster-portal / service-account-self-service;
// minted into the token by SpaTokenMinter, mapped 1:1 by SpaJwtConverters with an empty prefix).
import type { Role } from "../shell/nav";

/** Authority string (the value carried in the JWT `authorities` claim) → nav Role. */
const ROLE_BY_AUTHORITY: Record<string, Role> = {
  "ROLE_system-full-access": "admin",
  "ROLE_service-account-self-service": "self-service",
  "ROLE_foster-portal": "foster",
};

// Precedence when a principal carries more than one recognised authority: an admin landing beats
// self-service, which beats the foster portal. Highest-privilege wins so the shell shows the fullest
// menu the token allows (mirrors MainView resolving the most capable landing).
const PRECEDENCE: readonly Role[] = ["admin", "self-service", "foster"];

/**
 * Resolve ALL recognised nav Roles from the JWT authorities, in precedence order (highest-privilege
 * first), deduped. Empty when no authority maps. jmix review 3a-T2: the shell's nav is additive over
 * these, so a multi-role principal sees the union of their groups (Jmix menu visibility was
 * additive-by-policy).
 */
export function rolesFromAuthorities(authorities: readonly string[] | null | undefined): Role[] {
  if (!authorities || authorities.length === 0) {
    return [];
  }
  const present = new Set<Role>();
  for (const authority of authorities) {
    const role = ROLE_BY_AUTHORITY[authority];
    if (role) {
      present.add(role);
    }
  }
  return PRECEDENCE.filter((role) => present.has(role));
}

/**
 * Resolve the PRIMARY nav Role from the JWT authorities (the precedence winner) — drives the landing
 * page + brand sub-text. Returns `null` when no recognised authority is present (an authenticated
 * token with an unmapped role) so the caller can fail visibly rather than silently granting the foster
 * portal. Nav VISIBILITY uses {@link rolesFromAuthorities} (the full set), not just this winner.
 */
export function roleFromAuthorities(authorities: readonly string[] | null | undefined): Role | null {
  return rolesFromAuthorities(authorities)[0] ?? null;
}
